import { mdiArrowLeft } from '@mdi/js';

import React, { useState, useEffect } from 'react';

import { format } from 'date-fns';

import PropTypes from 'prop-types';
import { Document, Page, pdfjs } from 'react-pdf';
import { Modal, CodeValidator } from '~/shared/components';
import DownloadInMemoryLink from '~/shared/components/downloadButton';
import LoaderComponent from '~/shared/components/loader';

import {
  Container,
  HeaderContainer,
  Header,
  Title,
  NavContainer,
  PrescriptionBackgroundHeader,
  StyledIcon,
  IconContainer,
  PrescriptionNavContainer,
  BorderBottom,
  QrCodeNavContainer,
  OptionNav,
  AnimationContainer,
  IconClick,
  PdfContainer,
  NotFoundReaderPdfButton,
} from './styles';

import Qrcode from '../qrcode';
import Receipt from '../receipt';
import Help from '~/modules/patient/pages/help';
import api from '~/services/api';
import GlobalStyles from '~/styles/global';
import { LogoImage } from '~/shared/components/logoImage';
import validateHashOrigin from '~/utils';
import {
  getDocumentAuthorizationToken,
  setDocumentAuthorizationToken,
} from '~/utils/documentAuthorization';

function buildShowingData(cardData) {
  return {
    patientName: cardData.patient?.name ?? null,
    date: cardData.datetime
      ? format(new Date(cardData.datetime), 'dd/MM/yyyy')
      : null,
    doctorArticle: 'Dr(a).',
    doctorName: cardData.doctor?.name ?? null,
    doctorClassCodeType: cardData.doctor?.class_code?.classification ?? null,
    doctorClassCode: cardData.doctor?.class_code?.code ?? null,
    doctorClassOrigin: cardData.doctor?.class_code?.origin ?? null,
  };
}

function Prescription({
  title = 'Receita digital',
  token = '',
  goBack,
  id = null,
  validationKey = {},
}) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

  const [isEnabled, setAsEnabled] = useState(false);
  const [tab, setTab] = useState('receituario');
  const [documentsData, setDocumentsdata] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [prescriptionData, setPrescriptionData] = useState(null);
  const [prescriptionItems, setPrescriptionItems] = useState([]);
  const [showingData, setShowingData] = useState(null);
  const [modalIsOpen, setModalAsOpen] = useState(false);
  const [validationCode, setValidationCode] = useState(false);
  const [numPages, setNumPages] = useState(2);
  const [pdfOpen, setpdfOpen] = useState(false);
  const [pdfModalTitle, setPdfModalTitle] = useState('');
  const [pdfItems, setpdfItems] = useState('');
  const [pdfItemShowing, setpdfItemShowing] = useState('');
  const [qrCodeValidation, setQrCodeValidation] = useState(false);
  const [isUnlockDocumentView, setUnlockDocumentView] = useState(false);

  useEffect(() => {
    const cardData = prescriptionData || documentsData;
    if (!cardData) return;

    setShowingData(buildShowingData(cardData));
    setPrescriptionItems(documentsData.data);
  }, [prescriptionData, documentsData]);

  useEffect(() => {
    api
      .get(`/document/${validateHashOrigin(token)}/${id}`)
      .then(({ data: responseData }) => {
        setDocumentsdata(responseData);
      });
  }, [token, id]);

  const handleOpenPdf = () => {
    setPdfModalTitle('Receitas emitidas');
    const accessToken = getDocumentAuthorizationToken();

    api
      .get(`/document/file/${validateHashOrigin(token)}/${id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then(({ data: responseData }) => {
        const response = responseData.map((obj, index) => ({
          ...obj,
          title: `${obj.title} ${index + 1}`,
        }));
        setpdfItemShowing(responseData[0].url);
        setpdfItems(response);
        setpdfOpen(true);
      });
  };

  const postData = async (value) => {
    const { data: responseData } = await api.post(
      `/document/doctor/${validateHashOrigin(token)}/${id}`,
      { validation_key: value }
    );

    setPrescriptionData(responseData);
    setModalAsOpen(!modalIsOpen);
    setValidationCode(true);
    setDocumentAuthorizationToken(responseData?.accessToken);
    setUnlockDocumentView(true);

    if (qrCodeValidation) {
      handleOpenPdf();
    }
  };

  const onDocumentLoadSuccess = ({ numPages: loadedPages }) => {
    setNumPages(loadedPages);
  };

  if (helpOpen) {
    return (
      <Container>
        <Help setClose={() => setHelpOpen(false)} token={token} />
      </Container>
    );
  }

  return (
    <>
      <GlobalStyles />
      <Container>
        <HeaderContainer>
          <PrescriptionBackgroundHeader BackgroundHeight="188px" />
          <Header>
            <IconContainer>
              <IconClick onClick={() => goBack()}>
                <StyledIcon size={1} icon={mdiArrowLeft} />
              </IconClick>
            </IconContainer>
            <LogoImage />
          </Header>
        </HeaderContainer>
        <Title>{title}</Title>
        <NavContainer>
          <PrescriptionNavContainer>
            <OptionNav
              isDisabled={!isEnabled}
              onClick={() => {
                if (tab !== 'receituario') {
                  setAsEnabled(!isEnabled);
                  setTab('receituario');
                }
              }}
            >
              Receituário
            </OptionNav>
            <BorderBottom isDisabled={!isEnabled} />
          </PrescriptionNavContainer>
          <QrCodeNavContainer>
            <OptionNav
              isDisabled={isEnabled}
              onClick={() => {
                if (tab !== 'qrcode') {
                  setAsEnabled(!isEnabled);
                  setTab('qrcode');
                }
              }}
            >
              QR CODE
            </OptionNav>
            <BorderBottom isDisabled={isEnabled} />
          </QrCodeNavContainer>
        </NavContainer>

        {tab === 'receituario' && (
          <AnimationContainer>
            <Receipt
              documents={documentsData}
              token={token}
              id={id}
              validationKey={validationKey}
              prescriptionData={prescriptionData}
              setPrescriptionData={setPrescriptionData}
              showingData={showingData}
              prescriptionItems={prescriptionItems}
              setModalAsOpen={setModalAsOpen}
              validationCode={validationCode}
              setpdfOpen={handleOpenPdf}
              isUnlockDocumentView={isUnlockDocumentView}
            />
          </AnimationContainer>
        )}

        {tab === 'qrcode' && (
          <AnimationContainer>
            <Qrcode
              documents={documentsData}
              token={token}
              id={id}
              setOpen={(isOpen) => setHelpOpen(isOpen)}
              validationKey={validationKey}
              prescriptionData={prescriptionData}
              setPrescriptionData={setPrescriptionData}
              showingData={showingData}
              prescriptionItems={prescriptionItems}
              setModalAsOpen={setModalAsOpen}
              validationCode={validationCode}
              setQrCodeValidation={setQrCodeValidation}
              setpdfOpen={handleOpenPdf}
            />
          </AnimationContainer>
        )}

        <Modal
          isOpen={modalIsOpen}
          widthContainer="350px"
          heightContainer="330px"
          onCloseRequest={(event) => {
            event.preventDefault();
            setModalAsOpen(!modalIsOpen);
          }}
        >
          <div>
            {documentsData && (
              <CodeValidator
                width="165px"
                title="Desbloquear"
                fontSize="16px"
                height="50px"
                validationKey={validationKey}
                postData={(value) => postData(value)}
              />
            )}
          </div>
        </Modal>

        <Modal
          isPdfShowing="true"
          widthContainer="100%"
          headerSize="100vw"
          heightContainer="calc(100vh + 300px)"
          isOpen={pdfOpen}
          title={pdfModalTitle}
          onCloseRequest={(event) => {
            event.preventDefault();
            setpdfOpen(false);
          }}
          itens={pdfItems}
          onClickItens={(item) => {
            setpdfItemShowing(item.url);
          }}
        >
          <PdfContainer>
            <Document
              file={pdfItemShowing}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<LoaderComponent />}
              height="auto"
              width="90vw"
              renderMode="canvas"
            >
              {[1, 2].map((page) => (
                <Page
                  key={`page_${page + 1}`}
                  pageNumber={page >= 0 ? page : 0}
                />
              ))}
            </Document>

            <NotFoundReaderPdfButton>
              <DownloadInMemoryLink url={pdfItemShowing}>
                Fazer o download
              </DownloadInMemoryLink>
            </NotFoundReaderPdfButton>
          </PdfContainer>
        </Modal>
      </Container>
    </>
  );
}

Prescription.propTypes = {
  title: PropTypes.string.isRequired,
  goBack: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  validationKey: PropTypes.object.isRequired,
};

export default Prescription;
